// src/managers/ChatFeedManager/GetSummary.jsx
import { platformCannon } from './PlatformCannon';
import { generateId } from '@/managers/ConvoManager/ConvoHelpers';
import { getPreflightInjectables } from './PreflightInjectables';
import { keyStateManager } from '@/managers/zKeysManager';

const LOG_PREFIX = '📊 [Summary]';

// Now we can use proper structured output
const SUMMARY_SCHEMA = {
    type: "object",
    properties: {
        summary: {
            type: "string",
            description: "A detailed context preservation summary in markdown format"
        }
    },
    required: ["summary"]
};

const SYSTEM_INSTRUCTION = `You are creating a detailed context preservation summary for continuing this conversation later.

BE EXTREMELY DETAILED and include:
1. Current State - What problem/task/discussion was in progress
2. Context - Key decisions, implementation details, constraints
3. Progress Track - Problems solved, pending, failed attempts
4. Next Steps - Immediate actions, known challenges, user preferences
5. Evolution Tracking - Changes in direction or goals during conversation
6. Nuance - Subtle clarifications, user preferences, important details

Format your response as clean markdown in a JSON key. Start with "### Context Summary" as the header.`;

/* ========================
 * getChatSummary - Uses proper Gemini structured output
 * ======================== */
export async function getChatSummary(conversation, userId, platform, modelOverride = null) {
    if (!conversation) throw new Error('Conversation data is required');
    if (!userId) throw new Error('User ID is required');
    if (!platform) throw new Error('Platform is required');

    const finalUserPrompt = {
        id: generateId('sum_usr_msg'),
        role: 'user',
        rawContent: 'Based on your instructions, provide a summary of our conversation so far.',
        timestamp: new Date(),
        files: [],
    };

    const { processedConversation, appendToSystemMessage } = await getPreflightInjectables(
        conversation,
        finalUserPrompt,
        platform
    );
    
    if (processedConversation.length === 0) {
        throw new Error('No messages found to summarize');
    }

    const fullConversationPrompt = processedConversation.map(msg => {
        let content = msg.processedLlmContent || msg.rawContent || '';
        if (msg.tool_calls) {
            content += `\n[Assistant decided to use tools: ${msg.tool_calls.map(tc => tc.name).join(', ')}]`;
        }
        return `${msg.role === 'user' ? 'User' : 'Assistant'}: ${content}`;
    }).join('\n\n');

    const finalPromptForModel = `${appendToSystemMessage}\n\n${fullConversationPrompt}`;

    const model = modelOverride || platform.toUpperCase() === 'OPENAI'
        ? 'gpt-4o-mini'
        : platform.toUpperCase() === 'CLAUDE'
            ? 'claude-3-5-haiku-latest'
            : platform.toUpperCase() === 'OPENROUTER'
                ? 'openai/gpt-4o-mini'
                : 'gemini-2.5-flash-lite';

    const keyState = keyStateManager.getState();
    const apiKey = keyState.keys?.[platform.toUpperCase()] || null;
    if (!apiKey) {
        throw new Error(`No API key found for platform: ${platform}`);
    }

    // Use proper structured output
    const payloadForSingleCall = {
        userId: userId,
        platform: platform.toUpperCase(),
        model: model,
        apiKey: apiKey,
        prompt: finalPromptForModel,
        systemMessage: SYSTEM_INSTRUCTION,
        responseSchema: SUMMARY_SCHEMA,
    };
    
    try {
        const result = await platformCannon.invokeService('/singlecall', payloadForSingleCall);

        if (!result.success || !result.payload?.summary) {
            const errorMessage = result.error?.message || 'The request was not successful or did not return a valid summary.';
            throw new Error(`Single-call Error: ${errorMessage}`);
        }
        
        return {
            summary: result.payload.summary,
            platform: platform,
            model: model,
            messageCount: processedConversation.length,
        };

    } catch (error) {
        const errorMessage = error?.message || 'An unknown error occurred during summary generation.';
        console.error(`${LOG_PREFIX} Summary generation failed:`, error);
        throw new Error(`Failed to generate summary with ${platform}. Error: ${errorMessage}`);
    }
}